import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tooltip } from "@/uiKit/atoms/Tooltip.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type PhoneButtonProps = {
  patientId: string;
};

export const PhoneButton: React.FC<PhoneButtonProps> = ({ patientId }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  const patient = Queries.practitioner.usePatient(patientId);

  return (
    <Tooltip
      id="manage-patient-phone-button"
      className={styles.container}
      floaterClassName={styles.phoneTooltip}
      content={
        patient?.contactPhone ? (
          <a id="manage-patient-phone-call" href={`tel://${patient.contactPhone}`} className={styles.phoneAction}>
            <Typography variant="body">{patient.contactPhone}</Typography>
          </a>
        ) : (
          <Typography variant="body" className={styles.phoneNone}>
            {t("patientInfo.noPhoneNumber")}
          </Typography>
        )
      }
      placement="bottom-end"
    >
      <Icons.phoneOutline className={styles.phoneIcon} />
    </Tooltip>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    margin-left: ${theme.spacing(12)};
    position: relative;
    :hover {
      svg {
        opacity: 0.7;
      }
    }
  `,
  phoneIcon: css`
    color: ${theme.palette.primary.main};
    width: ${FontSizes.h4};
    height: ${FontSizes.h4};
  `,
  phoneTooltip: css`
    padding: 0;
    cursor: pointer;
  `,
  phoneNone: css`
    padding: ${theme.spacing(4)} ${theme.spacing(8)};
  `,
  phoneAction: css`
    display: flex;
    padding: ${theme.spacing(4)} ${theme.spacing(8)};
    cursor: pointer;
    &:hover {
      background-color: ${theme.palette.action.hover};
    }
  `,
});
